import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSupported, getSubscriptionState, subscribeToPush } from '@/lib/push';
import { isNative, isIOS } from '@/lib/platform';

const DISMISS_KEY_PREFIX = 'kinsy_push_prompt_dismissed_';
const SHOW_DELAY_MS = 1500;

/**
 * Detect whether the app is running as an installed PWA (opened from
 * the iPhone Home Screen icon, not from Safari's tab strip). iOS
 * Safari only allows Web Push when running in this standalone display
 * mode — asking for permission from within a Safari tab is a no-op.
 *
 * Two equivalent signals; either indicates standalone:
 *   - `matchMedia('(display-mode: standalone)').matches` (modern)
 *   - `navigator.standalone === true`                     (iOS legacy)
 */
function isStandalone() {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
  } catch (_) { /* SSR / private-mode safety */ }
  return false;
}

export default function PushPermissionPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);

  // Hide this prompt entirely on native iOS (Capacitor/TestFlight).
  // That app has its own dedicated APNs flow on ProfilePage. Web Push
  // (VAPID) doesn't fire there anyway, so showing this dialog would
  // look like it worked but never deliver a push.
  const nativeIOS = isNative() && isIOS();

  useEffect(() => {
    if (nativeIOS) return;
    if (!user?.id) return;
    // iOS Safari only supports Web Push in installed-PWA (standalone)
    // mode. On a plain Safari tab, `Notification.requestPermission()`
    // is a no-op or blocked, so silently skipping is the correct UX —
    // asking for something the OS won't deliver is worse than not
    // asking at all.
    if (isIOS() && !isStandalone()) return;
    if (!isPushSupported()) return;

    const dismissKey = `${DISMISS_KEY_PREFIX}${user.id}`;
    if (localStorage.getItem(dismissKey)) return;

    let cancelled = false;
    (async () => {
      const { permission, subscribed } = await getSubscriptionState();
      if (cancelled) return;
      // Only prompt if user hasn't decided yet AND isn't already subscribed.
      if (permission === 'default' && !subscribed) {
        setTimeout(() => !cancelled && setOpen(true), SHOW_DELAY_MS);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, nativeIOS]);

  // Second, hard render guard — even if `open` somehow got set to true
  // (e.g. React StrictMode double-invocation before the effect gate
  // fired) the Dialog will never mount on the native iOS wrapper.
  if (nativeIOS) return null;

  const dismiss = () => {
    if (user?.id) {
      localStorage.setItem(`${DISMISS_KEY_PREFIX}${user.id}`, String(Date.now()));
    }
    setOpen(false);
  };

  const enable = async () => {
    setEnabling(true);
    try {
      // `subscribeToPush()` (in lib/push.js) internally calls
      // `Notification.requestPermission()`, then registers the SW
      // and creates the PushSubscription, then POSTs it to the
      // backend for MongoDB storage.
      await subscribeToPush();
      toast.success('Notifications enabled');
      dismiss();
    } catch (err) {
      const msg = err?.message || 'Could not enable notifications';
      if (/denied/i.test(msg)) {
        dismiss();
        toast.error('Notifications were blocked. You can re-enable them in Settings → Kinsy → Notifications.');
      } else {
        toast.error(msg);
      }
    } finally {
      setEnabling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent
        data-testid="push-permission-prompt"
        className="max-w-sm rounded-3xl"
      >
        <div className="text-center py-2 space-y-4">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center">
            <Bell className="w-7 h-7 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-2">Stay connected with your family</h2>
            <p className="text-sm text-stone-600 leading-relaxed">
              Get notified when family shares new memories, reacts to your posts, or on this day in history.
            </p>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={enable}
              disabled={enabling}
              className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium"
              data-testid="push-enable-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              {enabling ? 'Enabling…' : 'Enable Notifications'}
            </Button>
            <Button
              variant="ghost"
              onClick={dismiss}
              className="w-full h-11 text-stone-500 rounded-full"
              data-testid="push-dismiss-btn"
            >
              <BellOff className="w-4 h-4 mr-2" />
              Maybe Later
            </Button>
          </div>
          <p className="text-[11px] text-stone-400">
            You can change this anytime in your profile.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
